import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import type { User } from '../types/user';
import { authService } from '../services/authService';
import { db } from '../services/firebase';
import { watchCentralAccess, type CentralState } from '../services/accessClient';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
interface AuthContextType { user: User | null; loading: boolean; isAdmin: boolean; isPendingAccess: boolean; accessError: boolean }
const AuthContext = createContext<AuthContextType | undefined>(undefined);
const roles: Record<string, User['role']> = { master: '최고관리자', newcomer: '새가족담당', finance: '재정담당', education: '교육담당' };
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null), [loading, setLoading] = useState(true), [isPendingAccess, setPendingAccess] = useState(false), [accessError, setAccessError] = useState(false);
  useEffect(() => {
    let generation = 0, cleanup: (() => void) | undefined, alive = true;
    const unsubscribe = authService.onAuthChange(async (firebaseUser) => {
      const current = ++generation; cleanup?.(); cleanup = undefined; setUser(null); setPendingAccess(false); setAccessError(false);
      if (!firebaseUser) { setPendingAccess(false); setAccessError(false); setLoading(false); return; }
      setLoading(true);
      try {
        if (firebaseUser.providerData.some((provider) => provider.providerId === 'password') && !firebaseUser.emailVerified) {
          await authService.signOutUser();
          if (alive && current === generation) { setPendingAccess(false); setAccessError(false); setLoading(false); }
          return;
        }
        await authService.createOrUpdateUser(firebaseUser);
        if (!alive || current !== generation) return;
        let central: CentralState = { enabled: false, active: false, grant: null };
        let profile: { role?: User['role']; isActive?: boolean } = {}, permission: { role?: User['role'] } = {};
        const publish = () => {
          if (!alive || current !== generation || central.ready !== true) return;
          const mappedCentralRole = roles[central.grant?.role || ''];
          const role = central.enabled ? (central.active && mappedCentralRole ? mappedCentralRole : '일반') : (profile.isActive === true && profile.role === permission.role ? permission.role : undefined);
          setUser(role ? { uid: firebaseUser.uid, email: firebaseUser.email, displayName: firebaseUser.displayName, role } : null);
          setPendingAccess(central.enabled ? (!central.active || !mappedCentralRole) : (!role || role === '일반'));
          setAccessError(central.enabled && central.error === true);
          setLoading(false);
        };
        const offProfile = onSnapshot(doc(db, 'users', firebaseUser.uid), (value) => { profile = value.data() || {}; publish(); }, () => { profile = {}; publish(); });
        const offPermission = onSnapshot(doc(db, 'user-permissions', firebaseUser.uid), (value) => { permission = value.data() || {}; publish(); }, () => { permission = {}; publish(); });
        const offCentral = watchCentralAccess(firebaseUser, 'administration', (value) => { central = value; publish(); });
        cleanup = () => { offProfile(); offPermission(); offCentral(); };
      } catch {
        if (alive && current === generation) {
          setUser({ uid: firebaseUser.uid, email: firebaseUser.email, displayName: firebaseUser.displayName, role: '일반' });
          setPendingAccess(true);
          setAccessError(true);
          setLoading(false);
        }
      }
    });
    return () => { alive = false; generation++; cleanup?.(); unsubscribe(); };
  }, []);
  if (loading) return <LoadingSpinner />;
  return <AuthContext.Provider value={{ user, loading, isAdmin: user?.role === '최고관리자', isPendingAccess, accessError }}>{children}</AuthContext.Provider>;
};
export const useAuth = () => { const value = useContext(AuthContext); if (!value) throw new Error('useAuth는 AuthProvider 안에서만 사용해야 합니다.'); return value; };
